
import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import swal from "sweetalert";
import Loader from "../Common/Loader";
import {
    getUnitOfDoseAction,
  deleteUnitOfDoseAction,
} from "../../../store/actions/UnitOfDoseAction";

const UnitOfDose = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isFirstLoad = useRef(true);

  const units = useSelector((state) => state.unitDose.units);
  const totalRows = useSelector((state) => state.unitDose.num_rows);

  const [searchQueries, setSearchQueries] = useState({
    unit_text: "",
    unit_status: "",
  });
  let searchTimeout = null;

  const [activeSearchField, setActiveSearchField] = useState("");
  const [activeSearchKeyword, setActiveSearchKeyword] = useState("");

  const [sortField, setSortField] = useState("unit_text");
  const [sortOrder, setSortOrder] = useState("asc");

  const [currentPage, setCurrentPage] = useState(0);
  const rowsPerPage = 10;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchUnits();
  }, []);

  const fetchUnits = async (
    searchBy = "",
    keyword = "",
    page = 0,
    sortBy = sortField,
    order = sortOrder
  ) => {
    try {
      // ✅ Show loader only on first visit
      if (isFirstLoad.current) {
        setLoading(true);
      }
  
      await dispatch(
        getUnitOfDoseAction(true, page, searchBy, keyword, sortBy, order)
      );
    } catch (err) {
      console.error("Failed to fetch units:", err);
      setError("Failed to load units.");
    } finally {
      setLoading(false);
      isFirstLoad.current = false; // ✅ stop loader forever
    }
  };
  
  

  const handleSearch = (e, field) => {
    const value = e.target.value;
  
    setSearchQueries({
      unit_text: "",
      unit_status: "",
      [field]: value,
    });
  
    setActiveSearchField(field);
    setActiveSearchKeyword(value);
    setCurrentPage(0);
  
    // ✅ debounce API call
    if (searchTimeout) clearTimeout(searchTimeout);
  
    searchTimeout = setTimeout(() => {
      fetchUnits(field, value, 0, sortField, sortOrder);
    }, 400);
  };
  

  // Sort
  const handleSort = (field) => {
    const order =
      sortField === field && sortOrder === "asc" ? "desc" : "asc";

    setSortField(field);
    setSortOrder(order);
    setCurrentPage(0); // reset to first page on new sort

    fetchUnits(activeSearchField, activeSearchKeyword, 0, field, order);
  };

  const handleDelete = (id) => {
    swal({
      title: "Are you sure?",
      text: "This unit will be deleted!",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (yes) => {
      if (yes) {
        try {
          setLoading(true);
          await dispatch(deleteUnitOfDoseAction(id));
          // Refresh the current page after deletion
          await fetchUnits(activeSearchField, activeSearchKeyword, currentPage, sortField, sortOrder);
        } catch (err) {
          setError("Failed to delete unit");
          console.error("Delete failed:", err);
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchUnits(activeSearchField, activeSearchKeyword, page);
  };

  const totalPages = Math.ceil(totalRows / rowsPerPage);

  return (
    <>
      <div className="row mb-3">
        <div className="col-12 text-end">
          <Link to="/app-add-unit-of-dose" className="btn btn-primary">
            + Add Unit of Dose
          </Link>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          {error && <div className="text-danger mb-2">{error}</div>}
          {loading ? (
            <Loader />
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-striped mb-4">
                  <thead className="table-light">
                    <tr>
                      <th onClick={() => handleSort("unit_text")} style={{ cursor: "pointer" }}>
                        Unit{" "}
                        {sortField === "unit_text" &&
                          (sortOrder === "asc" ? "↑" : "↓")}
                        <input
                          className="form-control form-control-sm mt-1"
                          value={searchQueries.unit_text}
                          onChange={(e) => handleSearch(e, "unit_text")}
                          onClick={(e) => e.stopPropagation()}
                          placeholder="Search Unit"
                        />
                      </th>

                      <th onClick={() => handleSort("unit_status")} style={{ cursor: "pointer" }}>
                        Status{" "}
                        {sortField === "unit_status" &&
                          (sortOrder === "asc" ? "↑" : "↓")}
                        <input
                          className="form-control form-control-sm mt-1"
                          value={searchQueries.unit_status}
                          onChange={(e) => handleSearch(e, "unit_status")}
                          onClick={(e) => e.stopPropagation()}
                          placeholder="Search Status"
                        />
                      </th>

                      <th>Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {units?.length ? (
                      units.map((item) => (
                        <tr key={item.id}>
                          <td>{item.unit_text}</td>
                          <td>
                            <span
                              className={`badge ${
                                item.unit_status === "Active" ? "bg-success" : "bg-danger"
                              }`}
                            >
                              {item.unit_status}
                            </span>
                          </td>
                          <td>
                            <div className="d-flex">
                              <i
                                className="fa fa-pencil text-success me-3"
                                onClick={() =>
                                  navigate("/app-add-unit-of-dose", { state: { item, returnTo: { currentPage, searchQueries, sortField, sortOrder } } })
                                }
                                style={{ cursor: "pointer" }}
                                title="Edit Unit"
                              />
                              <i
                                className="fa fa-trash text-danger"
                                onClick={() => handleDelete(item.id)}
                                style={{ cursor: "pointer" }}
                                title="Delete Unit"
                              />
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="3" className="text-center">
                          No Units Found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination (same as Branch) */}
              <div
                    className="pagination-wrapper"
                    style={{
                      display: "flex",
                      gap: "6px",
                      alignItems: "center",
                      justifyContent: "center",
                      marginTop: "20px",
                      flexWrap: "wrap",
                      flexDirection: "column",
                    }}
                  >
                    <div>
                      Showing {units?.length === 0 ? 0 : currentPage * rowsPerPage + 1} to{" "}
                      {Math.min((currentPage + 1) * rowsPerPage, totalRows)} of {totalRows} entries
                    </div>
                    <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", justifyContent: "center" }}>
                      <button
                        // onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 0))}
                        onClick={() => handlePageChange(Math.max(currentPage - 1, 0))}
                        disabled={currentPage === 0}
                        style={{
                          padding: "6px 12px",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          backgroundColor: "#fff",
                          color: "#000",
                          cursor: currentPage === 0 ? "not-allowed" : "pointer",
                          opacity: currentPage === 0 ? 0.5 : 1,
                        }}
                      >
                        &lt;
                      </button>
                      {(() => {
                        const pages = [];
                        const maxPagesToShow = 5;
                        const startPage = Math.max(
                          0,
                          Math.min(currentPage - 2, totalPages - maxPagesToShow)
                        );
                        const endPage = Math.min(startPage + maxPagesToShow, totalPages);

                        if (startPage > 0) {
                          pages.push(
                            <button
                              key={0}
                              onClick={() => handlePageChange(0)}
                              style={{
                                padding: "6px 12px",
                                border: "1px solid #ddd",
                                borderRadius: "4px",
                                backgroundColor: "#fff",
                                color: "#000",
                                cursor: "pointer",
                              }}
                            >
                              1
                            </button>
                          );
                          if (startPage > 1) {
                            pages.push(
                              <span
                                key="dots1"
                                style={{ padding: "6px 12px", userSelect: "none" }}
                              >
                                ...
                              </span>
                            );
                          }
                        }

                        for (let i = startPage; i < endPage; i++) {
                          const isActive = currentPage === i;
                          pages.push(
                            <button
                              key={i}
                              onClick={() => handlePageChange(i)}
                              style={{
                                padding: "6px 12px",
                                border: "1px solid #ddd",
                                borderRadius: "4px",
                                backgroundColor: isActive ? "#007bff" : "#fff",
                                color: isActive ? "#fff" : "#000",
                                cursor: "pointer",
                              }}
                            >
                              {i + 1}
                            </button>
                          );
                        }

                        if (endPage < totalPages) {
                          if (endPage < totalPages - 1) {
                            pages.push(
                              <span
                                key="dots2"
                                style={{ padding: "6px 12px", userSelect: "none" }}
                              >
                                ...
                              </span>
                            );
                          }
                          pages.push(
                            <button
                              key={totalPages - 1}
                              onClick={() => handlePageChange(totalPages - 1)}
                              style={{
                                padding: "6px 12px",
                                border: "1px solid #ddd",
                                borderRadius: "4px",
                                backgroundColor: "#fff",
                                color: "#000",
                                cursor: "pointer",
                              }}
                            >
                              {totalPages}
                            </button>
                          );
                        }

                        return pages;
                      })()}
                      <button
                        // onClick={() =>
                        //   setCurrentPage((prev) => Math.min(prev + 1, totalPages - 1))
                        // }
                        onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages - 1))}
                        disabled={currentPage === totalPages - 1}
                        style={{
                          padding: "6px 12px",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          backgroundColor: "#fff",
                          color: "#000",
                          cursor: currentPage === totalPages - 1 ? "not-allowed" : "pointer",
                          opacity: currentPage === totalPages - 1 ? 0.5 : 1,
                        }}
                      >
                        &gt;
                      </button>
                    </div>
                  </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default UnitOfDose;
